import Axios, { AxiosError } from "axios"
import { Operation } from "fast-json-patch"
import { uniq } from "lodash"
import dayjs, { Dayjs } from "utils/dayjs"

import ApiService from "./ApiService"
import { ApiLinkRel, ApiLinkEntity, ApiServiceName, Purpose, AvailabilityPeriodUnit, ErrorCode, SortDirection, ArticleSearchScope, AutocompleteField, GlobalAssetOverviewAutocompleteField, Currency } from "config/constants"
import Company, { ICompanyDto } from "models/Company"
import { Guid, IMultipleEntities, IMultipleEntitiesResponse, IApiLink, IApiFollowLink, IAssetLmidDto, Lmid } from "@/types"
import Asset, { AssetReferenceNumberType, AssetStatus, IGetAssetDto, IGetAssetFinancialDto, IGetAssetListPriceDto, IPostAssetDto } from "models/Asset"
import Reservation, { IGetReservationDto, IGetReservationNumbersItemDto, ReservationSortBy, ReservationStatus } from "models/Reservation"
import CustomError from "models/CustomError"
import ExtendAsSelfPopulatable from "./extend/ExtendAsSelfPopulatable"
import ImageCollection, { IGetImageCollectionDto } from "models/ImageCollection"
import ShippingDashboardItem, { IGetShippingDashboardItemDto } from "models/ShippingDashboardItem"
import AssetReservationPaged, { IGetAssetReservationPagedDto } from "models/AssetReservationPaged"
import UserService from "./UserService"
import { hasMorePages } from "./utils"
import { OutgoingInvoiceDraft, IGetOutgoingInvoiceDraftDto, OutgoingInvoiceDraftItem, IGetOutgoingInvoiceDraftItemDto, OutgoingInvoiceDraftSortBy } from "models/OutgoingInvoiceDraft"
import LabelCategories, { ICreateLabelCategoryDto, IGetLabelCategoriesDto } from "models/LabelCategories"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "models/AmountWithCurrency"
import LabelGroup, { IGetLabelGroupDto, IUpdateLabelGroupDto, IUpdateLabelGroupLocationDto, LabelStatus } from "models/LabelGroup"
import LabelsDashboard, { IGetLabelsDashboardDto } from "models/LabelsDashboard"
import AssetCategories, { IGetAssetCategoriesDto } from "models/AssetCategories"
import { ICreateAssetCategory } from "models/AssetCategory"
import AssetTypes, { IGetAssetTypesDto } from "models/AssetTypes"
import { ICreateAssetType } from "models/AssetType"
import { downloadBlobAsFile, downloadUrlAsFile, toDateTimeWithOffset, toDateTimeWithoutTimezone, toDateWithoutTimezone } from "utils/utils"
import NotificationAssetsNotShipped, { IGetNotificationAssetsNotShippedDto } from "models/NotificationAssetsNotShipped"
import NotificationAssetOutOfOrder, { IGetNotificationAssetOutOfOrderDto } from "models/NotificationAssetOutOfOrder"
import NotificationUnplannedAssetReleased, { IGetNotificationUnplannedAssetReleasedDto } from "models/NotificationUnplannedAssetReleased"
import { NotificationChannel, NotificationType } from "models/Notification"
import DocumentCategories, { IGetDocumentCategoriesDto } from "models/DocumentCategories"
import ConnectedAsset from "models/ConnectedAsset"
import ArticleService, { IGetArticleAssetsParams } from "./ArticleService"
import ShippingOrder, { IGetShippingOrderDto, ShippingOrderDisplayStatus, ShippingOrdersSortBy, ShippingOrderType } from "models/ShippingOrder"
import Unit from "models/Unit"
import { hydrateReservationConfiguration } from "stores/reservationConfiguraiton"
import ReservationArticle from "models/ReservationArticle"
import NotificationAssetDisposed, { IGetNotificationAssetDisposedDto } from "models/NotificationAssetDisposed"
import { IGetNotificationAnyDto, NotificationAny } from "models/NotificationAny"
import NotificationStoringCompleted, { IGetNotificationStoringCompletedDto } from "models/NotificationStoringCompleted"
import NotificationStoringIncomplete, { IGetNotificationStoringIncompleteDto } from "models/NotificationStoringIncomplete"
import NotificationAssetSplittingFailed, { IGetNotificationAssetSplittingFailedDto } from "models/NotificationAssetSplittingFailed"
import NotificationAssetSplittingSucceeded, { IGetNotificationAssetSplittingSucceededDto } from "models/NotificationAssetSplittingSucceeded"
import NotificationAssetReactivated, { IGetNotificationAssetReactivatedDto } from "models/NotificationAssetReactivated"
import NotificationReactivateAssetFailed, { IGetNotificationReactivateAssetFailedDto } from "models/NotificationReactivateAssetFailed"
import ExistingLmidData, { IGetExistingLmidDataDto } from "models/ExistingLmidData"
import NotificationUnplannedAssetStored, { IGetNotificationUnplannedAssetStoredDto } from "models/NotificationUnplannedAssetStored"
import { ICreateTenantWarehouseDto, ITenantWarehouseDto, TenantWarehouse } from "models/TenantWarehouse"
import TenantWarehouses, { IGetTenantWarehousesDto } from "models/TenantWarehouses"
import StocktakingOrders from "models/StocktakingOrders"
import { ICreateUpdateStocktakingOrderDto, IGetStocktakingOrderDto } from "models/StocktakingOrder"
import AssetVersion, { IGetAssetVersionDto } from "models/AssetVersion"
import { AssetCategoryImported, IGetAssetCategoryImportedDto } from "models/AssetCategoryImported"
import AssetImportOperation, { AssetImportError, IGetAssetImportOperationDto } from "models/AssetImportOperation"
import ErrorHandler from "utils/errorHandler"
import AssetImportItem, { IGetAssetImportItemDto } from "models/AssetImportItem"
import AssetProduct, { IGetAssetProductDto } from "models/AssetProduct"
import { hydrateInvoicePositions } from "stores/invoicePositions"
import NotificationManualReleaseCompleted, { IGetNotificationManualReleaseCompletedDto } from "models/NotificationManualReleaseCompleted"
import NotificationManualStoreCompleted, { IGetNotificationManualStoreCompletedDto } from "models/NotificationManualStoreCompleted"
import NotificationReservationCanceled, { IGetNotificationReservationCanceledDto } from "models/NotificationReservationCanceled"
import { OutgoingInvoice, ICreatePartialCreditNoteDto, IOutgoingInvoiceDto, OutgoingInvoiceSortBy, OutgoingInvoiceItem, IOutgoingInvoiceItemDto, ICreateFullCreditNoteDto, OutgoingInvoiceType, OutgoingInvoiceStatus, ReservationOutgoingInvoices, IGetReservationOutgoingInvoicesDto, ICreateCollectiveCreditNoteDto } from "models/OutgoingInvoice"
import AssetDisplayStatus, { IGetAssetDisplayStatusDto } from "models/AssetDisplayStatus"
import NotificationShippingOrderRejected, { IGetNotificationShippingOrderRejectedDto } from "models/NotificationShippingOrderRejected"
import NotificationShippingOrderChanged from "models/NotificationShippingOrderChanged"
import NotificationAdHocReservationCreated, { IGetNotificationAdHocReservationCreatedDto } from "models/NotificationAdHocReservationCreated"
import CustomerItem, { ICustomerItemDto } from "models/CustomerItem"
import StocktakingNotScannedAsset, { IGetStocktakingNotScannedAssetDto } from "models/StocktakingNotScannedAsset"
import StocktakingScope, { IGetStocktakingScopeDto } from "models/StocktakingScope"
import Color, { ICreateUpdateColorDto, IGetColorDto } from "models/Color"
import MonthlyRentalCost, { IMonthlyRentalCostDto } from "models/MonthlyRentalCost"
import TenantEmployee from "models/TenantEmployee"
import { ICollectiveReferenceNumberDto } from "models/CollectiveReferenceNumber"
import CollectiveReferenceNumbers, { IGetCollectiveReferenceNumbersDto, CollectiveReferenceNumbersRule } from "models/CollectiveReferenceNumbers"
import NotificationCollectiveReferenceNumberNotUpdated, { IGetNotificationCollectiveReferenceNumberNotUpdatedDto } from "models/NotificationCollectiveReferenceNumberNotUpdated"
import ShippingOrderOverviewItem, { IGetShippingOrderOverviewItemDto, ShippingMode } from "models/ShippingOrderOverviewItem"
import AssetArticle from "models/AssetArticle"
import NotificationCollectiveReferenceNumberUpdated, { IGetNotificationCollectiveReferenceNumberUpdatedDto } from "models/NotificationCollectiveReferenceNumberUpdated"
import NotificationReferenceNumbersUpdated, { IGetNotificationReferenceNumbersUpdatedDto } from "models/NotificationReferenceNumbersUpdated"
import { Offer, ICreateOffer, IGetOfferDto, IUpdateOffer, OfferSortBy, OfferStatus, OfferItem, IGetOfferItemDto } from "models/Offer"
import { AssetTableColumn } from "pages/InventoryOverviewPage/constants"
import AssetInspection, { ICreateAssetInspectionDTO, IGetAssetInspectionDTO, IUpdateAssetInspectionCommentDTO, IUpdateAssetInspectionDTO } from "models/AssetInspection"
import AssetInspectionPair from "models/AssetInspectionPair"
import NotificationAssetInspectionPerformed, { IGetNotificationAssetInspectionPerformedDto } from "models/NotificationAssetInspectionPerformed"
import RentToBuyChecklist, { IGetRentToBuyChecklistDTO, RentToBuyChecklistSortBy } from "models/RentToBuyChecklist"
import Commissions, { IGetCommissionsDto, IUpdateCommissionsDto } from "models/Commissions"
import AssetCollection, { IGetAssetCollectionDto } from "models/AssetCollection"
import NotificationAutomaticInvoicingFailed, { IGetNotificationAutomaticInvoicingFailedDto } from "models/NotificationAutomaticInvoicingFailed"
import { ICreateDocumentCategoryDto } from "models/DocumentCategory"
import { Document, IGetDocumentDto, IUpdateDocumentDTO } from "models/Document"
import ReservationPricingDetails from "models/ReservationPricingDetails"
import NotificationReleaseCompleted, { IGetNotificationReleaseCompletedDto } from "models/NotificationReleaseCompleted"
import AdHocOutgoingInvoiceDraft, { ICreateAdHocOutgoingInvoiceDraftDto, IGetAdHocOutgoingInvoiceDraftDto } from "models/AdHocOutgoingInvoiceDraft"
import AssetLabelGroup, { IGetAssetLabelGroupDto } from "models/AssetLabelGroup"
import ProfitCenterPermissions, { IGetProfitCenterPermissionsItemDto } from "models/ProfitCenterPermissions"
import { IGetGlobalAssetOverviewDto, IGetGlobalAssetOverviewParams, IGlobalAssetOverview } from "models/GlobalAssetOverview"
import { GlobalAssetOverviewItem, IGlobalAssetOverviewInitParams } from "models/GlobalAssetOverviewItem"
import { GlobalAssetOverviewAsset, IGetGlobalAssetOverviewAssetDto } from "models/GlobalAssetOverviewAsset"
import MaintenanceType, { ICreateMaintenanceTypeDto, IGetMaintenanceTypeDto } from "models/MaintenanceType"
import AssetMaintenance, { ICompleteAssetMaintenance, ICreateUpdateAssetMaintenance, IGetAssetMaintenanceDto, IGetAssetMaintenantcesParams } from "models/AssetMaintenance"
import NotificationAssetMaintenanceTimeOverdueSoon, { IGetNotificationAssetMaintenanceTimeOverdueSoonDto } from "models/NotificationAssetMaintenanceTimeOverdueSoon"
import NotificationAssetMaintenanceOperationHoursOverdueSoon, { IGetNotificationAssetMaintenanceOperationHoursOverdueSoonDto } from "models/NotificationAssetMaintenanceOperationHoursOverdueSoon"
import AssetLocation, { IGetAssetLocationDto } from "models/AssetLocation"
import OperationHoursRecord, { ICreateOperationHoursRecord, IGetOperationHoursRecordDto } from "models/OperationHoursRecord"
import WorkingDaysCalendars, { IGetWorkingDaysCalendarsDto } from "models/WorkingDaysCalendars"
import { ICreateUpdateWorkingDaysCalendarDto } from "models/WorkingDaysCalendar"
import Holidays, { IGetHolidaysDto } from "models/Holidays"
import Holiday, { ICreateUpdateHoliday } from "models/Holiday"
import CustomersDashboardItem, { IGetCustomersDashboardItemDto, IGetCustomersDashboardParams } from "models/CustomersDashboardItem"
import ErpCustomer, { IGetErpCustomerDto } from "models/ErpCustomer"
import tenantSettings from "stores/tenantSettings"

export interface IGetAssetReservationsParams {
    articleId?: Guid
    assetIds?: Guid[]
    startDate?: Dayjs
    endDate?: Dayjs
    pageSize?: number
    pageNumber?: number
    compact?: boolean
}

export interface IGetReservationsParams {
    customerNames?: string[]
    customerNumbers?: string[]
    crmReferenceNumbers?: string[]
    includeClosed?: boolean
    includeCanceled?: boolean
    purposes?: Purpose[]
    reservationIds?: Guid[]
    reservationNumbers?: string[]
    statuses?: ReservationStatus[]
    salesAdminNumbers?: string[]
    linedIds?: Guid[]
    familyIds?: Guid[]
    modelsIds?: Guid[]
    assetIds?: Guid[]
    warehouseIds?: Guid[]
    salesPersonNumbers?: string[]
    nodeAssetId?: Guid
    profitCenterIds?: (Guid | null)[]
    shortages?: boolean
    startsWithinDays?: number
    endsWithinDays?: number
    sortBy?: ReservationSortBy
    sortDirection?: SortDirection
    pageNumber?: number
    pageSize?: number
}

export interface IGetShippingOrderOverviewParams {
    pageNumber?: number
    pageSize?: number
    sortBy?: ShippingOrdersSortBy
    sortDirection?: SortDirection
    includeCompleted?: boolean
    types?: ShippingOrderType[]
    statuses?: ShippingOrderDisplayStatus[]
    orderNumbers?: string[]
    shippingOrderIds?: Guid[]
    reservationNumbers?: string[]
    customerNames?: string[]
    salesAdminNumbers?: string[]
    articleIds?: Guid[]
    assetIds?: Guid[]
    responsibleWarehouseIds?: (Guid | null)[]
    profitCenterIds?: (Guid | null)[]
    completeModes?: ShippingMode[]
}

export interface IGetOutgoingInvoicesParams {
    pageSize?: number
    pageNumber?: number
    sortBy?: OutgoingInvoiceSortBy
    sortDirection?: SortDirection
    includeAutomatic?: boolean
    disposalDateDiffersCreationPeriod?: boolean
    disposalDateDiffersInvoicePeriod?: boolean
    reservationNumbers?: string[]
    purposes?: Purpose[]
    invoiceTypes?: OutgoingInvoiceType[]
    erpOrderNumbers?: string[]
    erpInvoiceNumbers?: string[]
    invoiceStatuses?: OutgoingInvoiceStatus[]
    customerNumbers?: string[]
    customerNames?: string[]
    salesAdminNumbers?: string[]
    articleIds?: Guid[]
    assetIds?: Guid[]
    profitCenterIds?: (Guid | null)[]
    automaticInvoicing?: boolean
    invoiceDateDiffersCreationPeriod?: boolean
}

export interface IArticleAvailability {
    id: Guid
    assetsAvailableCount: number
    gaps: { start: Dayjs, end: Dayjs }[]
}

export interface IUpdateAssets {
    assetId: Guid
    referenceValue?: number | null
    homebaseId?: Guid | null
}

export interface GetReferenceValuesItem {
    assetId: Guid
    articleId: Guid
    lmid: Lmid
    articleDescription: string
    altArticleDescription?: string
    referenceValue?: AmountWithCurrency
    isLabeled?: boolean
    linkedToAssetCollection?: boolean
}

export interface IGetConnectedAssetsDto {
    assets: Guid[]
    links?: IApiLink[]
}

export type GetReferenceValuesResponse = IMultipleEntities<GetReferenceValuesItem, { referenceValueSum: number }>
export interface IGetAssetWarehouse {
    id: Guid | null
    description: string
}

export type ShippingOrderDeleteErrorPayload = {
    email: string
    date: Dayjs | null
}

export type IGetOutgoingInvoicesAggregates = {
    erpIncompleteInvoiceCount: number
    linkBulkSyncErpInvoices?: IApiLink
}
export type GetOutgoingInvoices = IMultipleEntities<OutgoingInvoiceItem, IGetOutgoingInvoicesAggregates>

export interface IGetStocktakingNotScannedAssetsDto extends IMultipleEntitiesResponse<IGetStocktakingNotScannedAssetDto> {
    stocktakingDueDate: string
    stocktakingScope: IGetStocktakingScopeDto
}

export interface IStocktakingNotScannedAssetsAggregate {
    stocktakingDueDate: Dayjs
    stocktakingScope: StocktakingScope
}

export interface IGetInvoiceDraftsParams {
    pageNumber?: number,
    pageSize?: number,
    sortBy?: OutgoingInvoiceDraftSortBy
    sortDirection?: SortDirection
    includeAutomatic?: boolean
    showAlwaysNextInvoice?: boolean
    reservationNumbers?: string[]
    purposes?: Purpose[]
    customerNumbers?: string[]
    customerNames?: string[]
    salesAdminNumbers?: string[]
    articleIds?: Guid[]
    assetIds?: Guid[]
    profitCenterIds?: (Guid | null)[]
    automaticInvoicing?: boolean
    invoicePendingMoreThanDays?: number
    lastInvoice?: boolean
    invoiceBlocked?: boolean
    InvoiceBeforeShipping?: boolean
}

export interface ICheckAssetsAvailabilityParams {
    articleIds?: Guid[]
    assetIds?: Guid[]
    skipReservationCartId?: Guid
    start: Dayjs
    end: Dayjs | null
    periodUnit?: AvailabilityPeriodUnit
    periodValue?: number
    purpose?: Purpose
    statuses?: AssetStatus[]
}

export interface IGetTenantWarehousesParams {
    articleId?: Guid
    start?: Dayjs
    end?: Dayjs | null
    skipReservationCartId?: Guid
    purpose?: Purpose
}

export interface ICheckArticlesAvailabilityParams {
    articleIds: Guid[]
    skipReservationCartId?: Guid
    start: Dayjs
    end?: Dayjs
    purpose?: Purpose
    periodUnit?: AvailabilityPeriodUnit
    periodValue?: number
    statuses?: AssetStatus[]
}

interface ICreateInvoiceAndNoteConflictErrorDto {
    reservationIsInEdit?: {
        links?: IApiLink[] | null
    } | null
    assetSplitOperationInProgress?: boolean | null
    blockedCustomer?: boolean | null
    notAllAssetsReleased?: boolean | null
    noRelevantMachineAssets?: boolean | null
    lastInvoiceCreated?: boolean | null
    invoicing?: boolean | null
    invoiceBlocked?: boolean | null
    canceledInvoice?: boolean | null
    noInvoicePositions?: boolean | null
    nodeAssetNotFixed?: boolean | null
    nodeAssetIncorrectDate?: boolean | null
    orderNumberRequired?: boolean | null
}

export interface ICreateInvoiceAndNoteConflictErrorPayload {
    reservationIsInEdit?: boolean
    reservationIsInEditUserEmail?: string
    assetSplitOperationInProgress?: boolean
    blockedCustomer?: boolean
    notAllAssetsReleased?: boolean
    noRelevantMachineAssets?: boolean
    lastInvoiceCreated?: boolean // not applicable for ad-hoc invoices
    invoicing?: boolean
    invoiceBlocked?: boolean
    canceledInvoice?: boolean
    noInvoicePositions?: boolean
    nodeAssetNotFixed?: boolean
    nodeAssetIncorrectDate?: boolean
    periodGap?: boolean // for collective credit notes
    orderNumberRequired?: boolean
}

export interface IGetNotificationsParams {
    pageSize?: number
    pageNumber?: number
    includeRead?: boolean
    includeDone?: boolean
    notificationDomains?: NotificationChannel[]
    notificationTypes?: NotificationType[]
    reservationNumbers?: string[]
    salesAdminNumbers?: string[]
    customerNames?: string[]
    customerNumbers?: string[]
    profitCenterIds?: (Guid | null)[]
}

export interface IGetOffersParams {
    pageNumber?: number
    pageSize?: number
    includeRejected?: boolean
    sortBy?: OfferSortBy
    sortDirection?: SortDirection
    offerNumbers?: string[]
    purposes?: Purpose[]
    statuses?: OfferStatus[]
    customerNames?: string[]
    customerNumbers?: string[]
    assetIds?: Guid[]
    scope?: ArticleSearchScope
    lineIds?: Guid[]
    familyIds?: Guid[]
    modelIds?: Guid[]
    warehouseIds?: Guid[]
    salesPersonNumbers?: string[]
    salesAdminNumbers?: string[]
    profitCenterIds?: (Guid | null)[]
}

export interface IExportInventoryOverviewParams extends Omit<IGetArticleAssetsParams, "pageNumber" | "pageSize"> {
    columns: AssetTableColumn[]
    language?: string | null
}

export interface IGetAssetInspectionsParams {
    assetId?: Guid
    reservationId?: Guid
    shippingOrderId?: Guid
}

export interface IExportConfigurationParams {
    language: string
    number?: string | null
    assets: {
        articleId: Guid
        assetId?: Guid | null
        fixedUnavailable?: boolean | null
        totalAcquisitionProductionCosts?: number | null
        acquisitionDate?: Dayjs | null
    }[]
}

class AssetService {
    #defaultCompanyCache: Company | null = null
    #colorsCache: Color[] | null = null
    #colorsRequest: Promise<Color[]> | null = null
    #customerCurrenciesCache: Currency[] | null = null
    #customerCurrenciesRequest: Promise<Currency[]> | null = null

    async getAsset(id: Guid, params: { withFinancial?: boolean, withDisposalEmail?: boolean } = {}): Promise<Asset | null> {
        try {
            const dto: IGetAssetDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.asset,
                    rel: ApiLinkRel.self,
                    params: { id }
                },
                includeEtag: true
            })

            let assetFinancialAndListPrice: {
                assetFinancial: IGetAssetFinancialDto
                assetListPrice?: IGetAssetListPriceDto | null
            } | null = null

            if (params.withFinancial) {
                const linkGetAssetFinancialAndListPrice = dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetFinancialAndListPrice && lnk.rel === ApiLinkRel.self)

                if (linkGetAssetFinancialAndListPrice) {
                    try {
                        assetFinancialAndListPrice = await ApiService.followLink({ link: linkGetAssetFinancialAndListPrice })
                    } catch (e) { }
                }
            }

            let disposedByEmail: string | undefined = undefined

            if (params.withDisposalEmail) {
                const linkGetDisposedByUserEmail = dto.links.find(lnk => lnk.entity === ApiLinkEntity.disposedByUserMail && lnk.rel === ApiLinkRel.self)

                if (linkGetDisposedByUserEmail && dto.disposal) {
                    try {
                        disposedByEmail = await UserService.getUserEmailByLink(linkGetDisposedByUserEmail) || undefined
                    } catch (e) { }
                }
            }

            const asset = Asset.fromDto(dto, {
                disposedByEmail,
                assetFinancial: assetFinancialAndListPrice?.assetFinancial,
                assetListPrice: assetFinancialAndListPrice?.assetListPrice ?? undefined,
            })

            return asset
        } catch (e) { console.log(e) }
        return null
    }

    async createAsset(link: IApiLink, data: IPostAssetDto): Promise<{ id: Guid, lmid: string }> {
        try {
            const resp: { id: Guid, lmid: string } = await ApiService.followLink({
                link,
                data,
            })
            return {
                id: resp.id,
                lmid: resp.lmid,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateAsset(link: IApiLink, data: IPostAssetDto, eTag: string = ""): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
                headers: {
                    "If-Match": eTag
                }
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateAssets(assets: IUpdateAssets[]): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.asset,
                    rel: ApiLinkRel.partialUpdate,
                },
                data: { assets },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetOprationHoursRecords(link: IApiLink): Promise<OperationHoursRecord[]> {
        try {
            const data: IGetOperationHoursRecordDto[] = await ApiService.followLink({ link })

            const userIds = uniq(data.map(r => r.recordedBy))
            const userEmails = userIds.length
                ? await UserService.getUserEmailsByIds(userIds)
                : {}

            const reservationIds = uniq(
                data
                    .map(r => r.reservationId)
                    .filter(rId => !!rId) as Guid[]
            )
            const reservations = reservationIds.length
                ? (await this.getReservations({ reservationIds, pageSize: reservationIds.length, includeCanceled: true, includeClosed: true })).list
                : []

            return data.map(dto => new OperationHoursRecord(dto, {
                recordedByEmail: dto.recordedBy ? userEmails[dto.recordedBy] : undefined,
                reservationNumber: reservations.find(r => r.id === dto.reservationId)?.number
            }))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async addAssetOprationHoursRecord(link: IApiLink, data: ICreateOperationHoursRecord): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: OperationHoursRecord.getCreateDto(data),
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetLocations({ assetId, pageNumber }: { assetId: Guid, pageNumber?: number }): Promise<IMultipleEntities<AssetLocation>> {
        try {
            const data: IMultipleEntitiesResponse<IGetAssetLocationDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetLocation,
                    rel: ApiLinkRel.all,
                    params: { id: assetId },
                    query: { pageSize: 1000, pageNumber },
                },
            })

            const userIds = uniq(
                data.entities
                    .map(l => l.createdBy)
                    .filter(createdBy => createdBy)
            ) as Guid[]
            const userEmails = await UserService.getUserEmailsByIds(userIds)

            return {
                list: data.entities.map(dto => new AssetLocation(dto, {
                    createdByEmail: dto.createdBy ? userEmails[dto.createdBy] : undefined
                })),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getAssetLocations({ assetId, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetCollection(assetIdOrLink: Guid | IApiLink): Promise<AssetCollection> {
        const assetId = typeof assetIdOrLink === "string" ? assetIdOrLink : undefined
        const linkGetCollection = typeof assetIdOrLink === "object" ? assetIdOrLink : undefined
        let assetCollectionDto: IGetAssetCollectionDto
        try {
            if (assetId) {
                assetCollectionDto = await ApiService.request({
                    serviceName: ApiServiceName.asset,
                    followLink: {
                        entity: ApiLinkEntity.assetCollection,
                        rel: ApiLinkRel.self,
                        params: { id: assetId },
                    },
                })
            } else if (linkGetCollection) {
                assetCollectionDto = await ApiService.followLink({ link: linkGetCollection })
            } else {
                throw new Error("Collection ID or link must be provided")
            }
            if (assetCollectionDto.assets.length === 0) {
                return new AssetCollection({ assets: [] })
            }
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response?.status === 404) {
                    return new AssetCollection({ assets: [] })
                }
            }
            ErrorHandler.handleHttpError(e)
        }

        try {
            const assetArticles = await ArticleService.getAssetArticles({
                assetIds: assetCollectionDto.assets,
                pageSize: assetCollectionDto.assets.length,
            })
            const connectedAssets: ConnectedAsset[] = []

            for (const collectionAssetId of assetCollectionDto.assets) {
                const assetArticle = assetArticles.list.find(aa => aa.assetId === collectionAssetId)
                if (assetArticle) {
                    connectedAssets.push(
                        new ConnectedAsset({
                            assetId: assetArticle.assetId,
                            articleId: assetArticle.articleId,
                            lmid: assetArticle.lmid,
                            labeled: assetArticle.isLabeled,
                            articleDescription: assetArticle.articleDescription || "",
                            altArticleDescription: assetArticle.altArticleDescription,
                            features: assetArticle.features,
                        })
                    )
                }
            }

            return new AssetCollection({
                assets: connectedAssets,
                links: assetCollectionDto.links,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateAssetCollection(link: IApiLink, assetIds: Guid[]): Promise<void> {
        try {
            await ApiService.followLink({ link, data: assetIds })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteAssetCollection(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetVersions(linkGetAssetVersions: IApiLink): Promise<AssetVersion[]> {
        try {
            const dto: IGetAssetVersionDto[] = await ApiService.followLink({
                link: linkGetAssetVersions,
            })
            return dto.map(avDto => new AssetVersion(avDto))
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getMonthlyRentalCosts(linkGetMonthlyRentalCosts: IApiLink): Promise<MonthlyRentalCost[]> {
        try {
            const resp: IMonthlyRentalCostDto[] = await ApiService.followLink({
                link: linkGetMonthlyRentalCosts,
            })
            return resp.map(dto => new MonthlyRentalCost(dto))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getDefaultCompany(): Promise<Company | null> {
        if (this.#defaultCompanyCache) {
            return this.#defaultCompanyCache
        }
        try {
            const dto: ICompanyDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.company,
                    rel: ApiLinkRel.default
                }
            })
            const company = Company.fromDTO(dto)
            this.#defaultCompanyCache = company
            return company
        } catch (e) { }

        return null
    }

    async getAssetPurposes(articleId?: Guid): Promise<IMultipleEntities<Purpose> | null> {
        try {
            const data: { purposes: Purpose[] } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetPurpose,
                    rel: ApiLinkRel.all,
                    params: {
                        query: articleId ? {
                            ArticleId: articleId,
                        } : ""
                    }
                }
            })
            return {
                list: data.purposes
            }
        } catch (e) { }

        return null
    }

    async getAssetReservations(params: IGetAssetReservationsParams): Promise<IMultipleEntities<AssetReservationPaged> | null> {
        try {
            const data: IMultipleEntitiesResponse<IGetAssetReservationPagedDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetReservation,
                    rel: ApiLinkRel.all
                },
                data: {
                    ...params,
                    startDate: params.startDate ? toDateTimeWithOffset(params.startDate) : undefined,
                    endDate: params.endDate ? toDateTimeWithOffset(params.endDate) : undefined,
                }
            })

            return {
                list: data.entities.map(dto => new AssetReservationPaged(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getAssetReservations({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) { }

        return null
    }

    async getReservations(params: IGetReservationsParams): Promise<IMultipleEntities<Reservation>> {
        try {
            const data: IMultipleEntitiesResponse<IGetReservationDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.reservation,
                    rel: ApiLinkRel.all
                },
                data: params
            })
            return {
                list: data.entities.map(dto => new Reservation(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getReservations({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getReservation({
        reservationId,
        withPricingDetails,
        hydrateConfiguration,
        hydrateInvoicePositions: shouldHydrateInvoicePositions,
    }: {
        reservationId: Guid
        withPricingDetails?: boolean
        hydrateConfiguration?: boolean
        hydrateInvoicePositions?: boolean
    }): Promise<Reservation> {
        try {
            const dto: IGetReservationDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.reservation,
                    rel: ApiLinkRel.self,
                    params: {
                        id: reservationId
                    }
                }
            })

            const reservation = new Reservation(dto)

            if (withPricingDetails && reservation.linkGetPricingDetails) {
                const pricingDetailsDto = await ApiService.followLink({ link: reservation.linkGetPricingDetails })
                const pricingDetails = new ReservationPricingDetails(pricingDetailsDto)

                if (shouldHydrateInvoicePositions && pricingDetails.invoicePositions && pricingDetails.invoicePositions.length) {
                    pricingDetails.invoicePositions = await hydrateInvoicePositions(pricingDetails.invoicePositions)
                }

                reservation.pricingDetails = pricingDetails
            }

            if (hydrateConfiguration && reservation.articles && reservation.articles.length) {
                reservation.articles = await hydrateReservationConfiguration<ReservationArticle>({
                    articles: reservation.articles,
                    linkGetShippingOrders: reservation.linkGetShippingOrders
                })
            }

            return reservation
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async deleteReservation(link: IApiLink, overrideOwnership?: boolean): Promise<void> {
        try {
            await ApiService.followLink({
                link: {
                    ...link,
                    href: `${link.href}${overrideOwnership ? "?OverrideOwnership=true" : ""}`
                }
            })
        } catch (e) {
            if (!e.isAxiosError) {
                throw new CustomError({
                    code: ErrorCode.systemError
                })
            }

            const error = e as AxiosError<any>

            if (error.response && (error.response.status === 403 || error.response.status === 409 || error.response.status === 423)) {
                const userEmailLink = error.response.data.links ? error.response.data.links.find((lnk: IApiLink) => lnk.entity === ApiLinkEntity.userMail && lnk.rel === ApiLinkRel.self) : null
                let email = ""
                if (userEmailLink) {
                    email = (await UserService.getUserEmailByLink(userEmailLink)) || ""
                }
                let code = ErrorCode.systemError
                switch (error.response.status) {
                    case 403:
                        code = ErrorCode.forbidden
                        break
                    case 409:
                        code = ErrorCode.conflict
                        break
                    case 423:
                        code = ErrorCode.locked
                }
                throw new CustomError({
                    code,
                    payload: {
                        email,
                        date: error.response.data.lu_date ? dayjs(error.response.data.lu_date) : null,
                    },
                })
            }

            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async reopenReservation(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async cancelReservation(link: IApiLink, reason: string, overrideOwnership?: boolean): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: {
                    reason,
                    overrideOwnership: !!overrideOwnership
                }
            })
        } catch (e) {

            if (!e.isAxiosError) {
                throw new CustomError({
                    code: ErrorCode.systemError
                })
            }

            const error = e as AxiosError<any>

            if (error.response && (error.response.status === 403 || error.response.status === 409 || error.response.status === 423)) {
                const userEmailLink = error.response.data.links ? error.response.data.links.find((lnk: IApiLink) => lnk.entity === ApiLinkEntity.userMail && lnk.rel === ApiLinkRel.self) : null
                let email = ""
                if (userEmailLink) {
                    email = (await UserService.getUserEmailByLink(userEmailLink)) || ""
                }
                let code = ErrorCode.systemError
                switch (error.response.status) {
                    case 403:
                        code = ErrorCode.forbidden
                        break
                    case 409:
                        code = ErrorCode.conflict
                        break
                    case 423:
                        code = ErrorCode.locked
                }
                throw new CustomError({
                    code,
                    payload: {
                        email,
                        date: error.response.data.lu_date ? dayjs(error.response.data.lu_date) : null,
                    },
                })
            }
            ErrorHandler.handleHttpError(e)
        }
    }

    async getReservationInvoices(reservationId: Guid, options: { hydrateInvoicePositions?: boolean } = {}): Promise<ReservationOutgoingInvoices> {
        try {
            const resp: IGetReservationOutgoingInvoicesDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.reservationInvoice,
                    rel: ApiLinkRel.all,
                    params: { id: reservationId }
                }
            })

            const result = new ReservationOutgoingInvoices(resp)

            if (options.hydrateInvoicePositions) {
                const tenantInvoicePositions = (await UserService.getTenantInvoicePositions()).entities
                result.invoices = result.invoices.map(invoice => {
                    invoice.invoicePositions = invoice.invoicePositions.map(ip => {
                        ip.invoicePosition = tenantInvoicePositions.find(tIp => tIp.number === ip.number)
                        return ip
                    })
                    return invoice
                })
            }

            return result
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getOutgoingInvoice(id: Guid): Promise<OutgoingInvoice> {
        try {
            const dto: IOutgoingInvoiceDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.invoice,
                    rel: ApiLinkRel.self,
                    params: { id }
                },
            })
            return new OutgoingInvoice(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async syncErpInvoice(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getReservationShippingOrders(link: IApiLink, units?: Unit[]): Promise<ShippingOrder[]> {
        try {
            const data: IGetShippingOrderDto[] = await ApiService.followLink({ link })
            return data.map(dto => new ShippingOrder({
                dto,
                sizeUnits: units
            }))
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async deleteShippingOrder(link: IApiLink, overrideOwnership?: boolean): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                linkQuery: {
                    OverrideOwnership: overrideOwnership
                }
            })
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response && (error.response.status === 403 || error.response.status === 409)) {
                    const userEmailLink = error.response.data.links
                        ? error.response.data.links.find((lnk: IApiLink) => lnk.entity === ApiLinkEntity.userMail && lnk.rel === ApiLinkRel.self)
                        : null
                    let email = ""
                    if (userEmailLink) {
                        email = (await UserService.getUserEmailByLink(userEmailLink)) || ""
                    }
                    let code = ErrorCode.systemError
                    switch (error.response.status) {
                        case 403:
                            code = ErrorCode.forbidden
                            break
                        case 409:
                            code = ErrorCode.conflict
                            break
                    }
                    throw new CustomError<ShippingOrderDeleteErrorPayload>({
                        code,
                        payload: {
                            email,
                            date: error.response.data.lu_date ? dayjs(error.response.data.lu_date) : null,
                        },
                    })
                }
            }

            ErrorHandler.handleHttpError(e)
        }
    }

    async checkArticlesAvailability({
        articleIds,
        skipReservationCartId,
        start,
        end,
        purpose,
        periodUnit,
        periodValue,
        statuses
    }: ICheckArticlesAvailabilityParams): Promise<IArticleAvailability[]> {
        try {
            if (articleIds.length === 0) {
                return []
            }
            const resp: {
                id: Guid,
                assetsAvailableCount: number,
                gaps: {
                    startGap: string,
                    endGap: string
                }[]
            }[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.availabilityArticle,
                    rel: ApiLinkRel.all,
                },
                data: {
                    articleIds,
                    start: toDateTimeWithOffset(start),
                    end: end ? toDateTimeWithOffset(end) : undefined,
                    purpose,
                    unit: periodUnit,
                    number: periodValue,
                    skipReservationCartId,
                    statuses,
                }
            })

            return resp.map(item => {
                return {
                    id: item.id,
                    assetsAvailableCount: item.assetsAvailableCount,
                    gaps: item.gaps.map(gap => ({
                        start: dayjs.utc(gap.startGap),
                        end: dayjs.utc(gap.endGap)
                    }))
                }
            })
        } catch (e) { }

        return []
    }

    async checkAssetsAvailability({
        articleIds,
        assetIds,
        skipReservationCartId,
        start,
        end,
        periodUnit,
        periodValue,
        purpose,
        statuses
    }: ICheckAssetsAvailabilityParams): Promise<Guid[]> {
        try {
            if (assetIds && assetIds.length === 0 && (!articleIds || articleIds.length === 0)) {
                return []
            }
            const availableAssetIds: Guid[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.availabilityAsset,
                    rel: ApiLinkRel.all,
                },
                data: {
                    articleIds,
                    assetIds,
                    start: toDateTimeWithOffset(start),
                    end: end ? toDateTimeWithOffset(end) : undefined,
                    purpose,
                    unit: periodUnit,
                    number: periodValue,
                    skipReservationCartId,
                    statuses
                }
            })
            return availableAssetIds
        } catch (e) { }

        return []
    }

    async splitAsset(link: IApiLink, {
        splitBookingPeriod,
        parentReferenceValue,
        children
    }: {
        splitBookingPeriod: string,
        parentReferenceValue: IAmountWithCurrencyDto,
        children: { articleId: Guid, referenceValue: IAmountWithCurrencyDto }[]
    }): Promise<void> {
        try {
            const body = {
                splitBookingPeriod: splitBookingPeriod,
                parentReferenceValue: parentReferenceValue,
                children: children
            }
            await ApiService.followLink({
                link,
                data: body
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async reactivateAsset(
        assetId: Guid,
        availabilityDate: Dayjs,
        reactivationBookingPeriod: Dayjs,
        reactivationReason?: string,
    ): Promise<{ success: boolean; error?: string }> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetReactivation,
                    rel: ApiLinkRel.new,
                    params: { id: assetId },
                },
                data: {
                    availabilityDate: toDateTimeWithoutTimezone(availabilityDate),
                    reactivationBookingPeriod: toDateWithoutTimezone(reactivationBookingPeriod),
                    ...(reactivationReason ? { reactivationReason } : {})
                },
            })
            return { success: true }
        } catch (e) {
            const axiosError = e as AxiosError<{ message?: string }>
            if (axiosError.response?.status === 409) {
                return { success: false, error: axiosError.response.data?.message }
            }
            if (axiosError.response?.status === 422) {
                return { success: false, error: axiosError.response.data?.message || "invalidAvailabilityDate" }
            }
            if (axiosError.response?.status === 404) {
                return { success: false, error: 'notFound' }
            }
            if (axiosError.response?.status === 403) {
                return { success: false, error: 'noPermission' }
            }
            ErrorHandler.handleHttpError(e)
            return { success: false }
        }
    }

    async getReactivationAvailabilityDateBounds(assetId: Guid): Promise<{ reactivationLowerBoundDate: Dayjs | null }> {
        try {
            const dto: { reactivationLowerBoundDate?: string | null } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetReactivationAvailabilityDateBounds,
                    rel: ApiLinkRel.self,
                    params: { id: assetId },
                },
            })

            return {
                reactivationLowerBoundDate: dto.reactivationLowerBoundDate ? dayjs.utc(dto.reactivationLowerBoundDate) : null,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
            return { reactivationLowerBoundDate: null }
        }
    }

    async getAssetImages(params: {
        assetId: Guid
        pageNumber?: number
        pageSize?: number
    }): Promise<IMultipleEntities<ImageCollection>> {
        try {
            const data: IMultipleEntitiesResponse<IGetImageCollectionDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetImage,
                    rel: ApiLinkRel.all,
                    query: {
                        PageNumber: params.pageNumber,
                        PageSize: params.pageSize,
                    },
                    params: {
                        id: params.assetId
                    }
                }
            })
            return {
                list: data.entities.map((dto: IGetImageCollectionDto) => new ImageCollection(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getAssetImages({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getReservationNumbers({ search, includeCanceled }: { search?: string, includeCanceled?: boolean }): Promise<IMultipleEntities<IGetReservationNumbersItemDto>> {
        return ApiService.getPagedData<IGetReservationNumbersItemDto, IGetReservationNumbersItemDto>({
            service: ApiServiceName.asset,
            entity: ApiLinkEntity.reservationNumber,
            rel: ApiLinkRel.all,
            mapFunc: dto => dto,
            query: {
                Criteria: search,
                IncludeCanceled: includeCanceled,
            },
        })
    }

    async getReservationStatuses(): Promise<IMultipleEntities<ReservationStatus>> {
        try {
            const statuses: ReservationStatus[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.reservationStatus,
                    rel: ApiLinkRel.all,
                },
            })

            return {
                list: statuses,
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async updateReservationComment(link: IApiLink, comment?: string): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: { comment }
            })
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            throw new CustomError({
                                code: ErrorCode.forbidden
                            })
                        case 404:
                            throw new CustomError({
                                code: ErrorCode.notFound
                            })
                        case 423:
                            throw new CustomError({
                                code: ErrorCode.locked
                            })
                    }
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getReservationPurposes(): Promise<IMultipleEntities<Purpose>> {
        try {
            const resp: Purpose[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.reservationPurpose,
                    rel: ApiLinkRel.all,
                },
            })

            return {
                list: resp
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getLmids(params: {
        search?: string,
        exact?: boolean,
    }): Promise<IMultipleEntities<IAssetLmidDto>> {
        return ApiService.getPagedData<IAssetLmidDto, IAssetLmidDto>({
            service: ApiServiceName.asset,
            entity: ApiLinkEntity.assetLmid,
            rel: ApiLinkRel.all,
            mapFunc: dto => dto,
            linkParams: {
                query: {
                    Criteria: params.search,
                    ExactSearch: params.exact,
                },
            },
        })
    }

    requestExistingLmid({
        search
    }: {
        search: string
    }): {
        controller: AbortController,
        promise(): Promise<ExistingLmidData>
    } {
        const controller = new AbortController()
        return {
            controller,
            promise: async () => {
                try {
                    const data: IGetExistingLmidDataDto = await ApiService.request({
                        serviceName: ApiServiceName.asset,
                        followLink: {
                            entity: ApiLinkEntity.lmid,
                            rel: ApiLinkRel.self,
                            params: {
                                query: {
                                    Lmid: search
                                }
                            }
                        },
                        signal: controller.signal
                    })

                    return new ExistingLmidData(data)
                } catch (e) {
                    if (Axios.isCancel(e)) {
                        throw new Error(ErrorCode.requestCanceled)
                    }
                    if (!e.isAxiosError) {
                        throw new Error(ErrorCode.unknownError)
                    }
                    const error = e as AxiosError<any>
                    if (error.response && error.response.status === 404) {
                        throw new Error(ErrorCode.notFound)
                    } else {
                        throw new Error(ErrorCode.systemError)
                    }
                }
            }
        }
    }

    async requestNewLmid({
        isExtManufacturer
    }: {
        isExtManufacturer: boolean
    }): Promise<{ lmid: string, createAssetLink: IApiLink } | null> {
        try {
            const data: { lmid: string, links: IApiLink[] } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.lmid,
                    rel: ApiLinkRel.new
                },
                data: {
                    isExtManufacturer
                }
            })

            const createAssetLink = data.links.find(lnk => lnk.entity === ApiLinkEntity.asset && lnk.rel === ApiLinkRel.new)

            if (!data.lmid || !createAssetLink) {
                return null
            }

            return {
                lmid: data.lmid,
                createAssetLink
            }
        } catch (e) { }
        return null
    }

    async getTempLmid({
        isExtManufacturer,
        internalOrderNumber,
    }: {
        isExtManufacturer: boolean
        internalOrderNumber: string
    }): Promise<{ lmid: string, createAssetLink: IApiLink | null }> {
        try {
            const data: { lmid: string, links: IApiLink[] } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.lmid,
                    rel: ApiLinkRel.new
                },
                data: {
                    isExtManufacturer,
                    internalOrderNumber,
                }
            })

            const createAssetLink = data.links.find(lnk => lnk.entity === ApiLinkEntity.asset && lnk.rel === ApiLinkRel.new)

            return {
                lmid: data.lmid,
                createAssetLink: createAssetLink || null
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getShippingDashboard(params: {
        pageNumber?: number,
        pageSize?: number,
    } = {}): Promise<IMultipleEntities<ShippingDashboardItem>> {
        try {
            const {
                pageNumber,
                pageSize,
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize || 10,
                PageNumber: pageNumber || 1,
            }

            const data: IMultipleEntitiesResponse<IGetShippingDashboardItemDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.shippingDashboard,
                    rel: ApiLinkRel.all,
                    query
                },
            })

            const articleIds = uniq(data.entities.map(dto => dto.articleId))
            const articleAssets = await ArticleService.getArticleAssets({
                articleIds,
                pageSize: articleIds.length,
            })
            const articleDescriptions = articleAssets
                ? articleAssets.list.reduce((acc, { id, description }) => {
                    acc[id] = description
                    return acc
                }, {} as { [articleId: string]: string })
                : {}

            return {
                list: data.entities.map(dto => new ShippingDashboardItem(dto, {
                    articleDescription: articleDescriptions[dto.articleId] || "",
                })),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getShippingDashboard({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getShippingOrderOverview(params: IGetShippingOrderOverviewParams = {}): Promise<IMultipleEntities<ShippingOrderOverviewItem>> {
        try {
            const data: IMultipleEntitiesResponse<IGetShippingOrderOverviewItemDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.shippingOrderOverview,
                    rel: ApiLinkRel.all,
                },
                data: params,
            })

            const assetIds = uniq(data.entities.map(dto => dto.mainAssetId))
            let assetArticles: { [assetId: string]: AssetArticle } = {}
            if (assetIds.length) {
                const assetArticlesResp = assetIds.length
                    ? (await ArticleService.getAssetArticles({
                        assetIds,
                        pageSize: assetIds.length,
                    })).list
                    : null
                if (assetArticlesResp?.length) {
                    assetArticles = assetArticlesResp.reduce((acc, aa) => {
                        acc[aa.assetId] = aa
                        return acc
                    }, {} as { [assetId: string]: AssetArticle })
                }
            }

            return {
                list: data.entities.map(dto => new ShippingOrderOverviewItem(dto, {
                    mainArticleDescription: assetArticles[dto.mainAssetId]?.articleDescription || "",
                    mainAssetLmid: assetArticles[dto.mainAssetId]?.lmid || "",
                    mainArticleId: assetArticles[dto.mainAssetId]?.articleId || "",
                })),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getShippingOrderOverview({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getInvoiceDrafts(params: IGetInvoiceDraftsParams = {}, options: {
        hydrateSalesAdmin?: boolean
    } = {}): Promise<IMultipleEntities<OutgoingInvoiceDraftItem>> {
        try {
            const data: IMultipleEntitiesResponse<IGetOutgoingInvoiceDraftItemDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.invoiceDraft,
                    rel: ApiLinkRel.all,
                },
                data: params,
            })

            let employees: TenantEmployee[] = []
            if (options.hydrateSalesAdmin) {
                if (!params.pageNumber || params.pageNumber === 1) {
                    employees = (await tenantSettings.fetchTenantEmployees()).entities
                } else {
                    employees = tenantSettings.$tenantEmployees.getState()?.entities || []
                }
            }

            return {
                list: data.entities.map(dto => new OutgoingInvoiceDraftItem(dto, {
                    salesAdminName: employees.find(e => e.number === dto.salesAdminNumber)?.fullName || "",
                })),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getInvoiceDrafts({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }, options) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }


    async getInvoiceDraft({
        reservationId
    }: {
        reservationId: Guid
    }): Promise<OutgoingInvoiceDraft> {
        try {
            const dto: IGetOutgoingInvoiceDraftDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.invoiceDraft,
                    rel: ApiLinkRel.self,
                    params: {
                        id: reservationId,
                    },
                },
            })
            const invoiceDraft = new OutgoingInvoiceDraft(dto)
            invoiceDraft.invoicePositions = await hydrateInvoicePositions(invoiceDraft.invoicePositions)
            return invoiceDraft
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getInvoiceDraftByDate({
        reservationId,
        start,
        end
    }: {
        reservationId: Guid
        start: string
        end: string
    }): Promise<OutgoingInvoiceDraft> {
        try {
            const dto: IGetOutgoingInvoiceDraftDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.invoiceDraftByDate,
                    rel: ApiLinkRel.self,
                    params: {
                        id: reservationId,
                        start,
                        end
                    },
                },
            })
            const invoiceDraft = new OutgoingInvoiceDraft(dto)
            invoiceDraft.invoicePositions = await hydrateInvoicePositions(invoiceDraft.invoicePositions)
            return invoiceDraft
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAdHocInvoiceDraft({
        reservationId
    }: {
        reservationId: Guid
    }): Promise<AdHocOutgoingInvoiceDraft> {
        try {
            const dto: IGetAdHocOutgoingInvoiceDraftDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.adHocInvoiceDraft,
                    rel: ApiLinkRel.self,
                    params: {
                        id: reservationId,
                    },
                },
            })
            return new AdHocOutgoingInvoiceDraft(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async #handleCreateInvoiceAndNoteErrors(e: any) {
        if (e.isAxiosError) {
            const error = e as AxiosError<any>
            if (error.response && error.response.status === 409) {
                const responseData: ICreateInvoiceAndNoteConflictErrorDto = error.response.data
                const errorConflict = new CustomError<ICreateInvoiceAndNoteConflictErrorPayload>({
                    code: ErrorCode.conflict,
                    payload: {},
                })

                if (responseData.reservationIsInEdit) {
                    errorConflict.payload!.reservationIsInEdit = true
                    const userEmailLink = responseData.reservationIsInEdit.links
                        ? responseData.reservationIsInEdit.links.find((lnk: IApiLink) => {
                            return lnk.entity === ApiLinkEntity.userMail && lnk.rel === ApiLinkRel.self
                        })
                        : null
                    if (userEmailLink) {
                        errorConflict.payload!.reservationIsInEditUserEmail = (await UserService.getUserEmailByLink(userEmailLink)) || ""
                    }
                }

                if (responseData.assetSplitOperationInProgress) {
                    errorConflict.payload!.assetSplitOperationInProgress = true
                }

                if (responseData.blockedCustomer) {
                    errorConflict.payload!.blockedCustomer = true
                }

                if (responseData.notAllAssetsReleased) {
                    errorConflict.payload!.notAllAssetsReleased = true
                }

                if (responseData.noRelevantMachineAssets) {
                    errorConflict.payload!.noRelevantMachineAssets = true
                }

                if (responseData.lastInvoiceCreated) {
                    errorConflict.payload!.lastInvoiceCreated = true
                }

                if (responseData.invoicing) {
                    errorConflict.payload!.invoicing = true
                }

                if (responseData.invoiceBlocked) {
                    errorConflict.payload!.invoiceBlocked = true
                }

                if (responseData.canceledInvoice) {
                    errorConflict.payload!.canceledInvoice = true
                }

                if (responseData.noInvoicePositions) {
                    errorConflict.payload!.noInvoicePositions = true
                }

                if (responseData.nodeAssetNotFixed) {
                    errorConflict.payload!.nodeAssetNotFixed = true
                }

                if (responseData.nodeAssetIncorrectDate) {
                    errorConflict.payload!.nodeAssetIncorrectDate = true
                }

                if (responseData.orderNumberRequired) {
                    errorConflict.payload!.orderNumberRequired = true
                }

                throw errorConflict
            }
        }
        ErrorHandler.handleHttpError(e)
    }

    async createOutgoingInvoice(data: {
        reservationId: Guid
        overrideOnlyMachinesWarning?: boolean
        orderNumber?: string | null
        sendToErp?: boolean
    }): Promise<Guid | undefined> {
        try {
            const resp: { id: Guid } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.invoice,
                    rel: ApiLinkRel.new,
                },
                data
            })
            return resp.id
        } catch (e) {
            await this.#handleCreateInvoiceAndNoteErrors(e)
        }
    }

    async createAdHocOutgoingInvoice(
        link: IApiLink,
        params: ICreateAdHocOutgoingInvoiceDraftDto
    ): Promise<Guid | undefined> {
        try {
            const resp: { id: Guid } = await ApiService.followLink({
                link,
                data: params,
            })
            return resp.id
        } catch (e) {
            await this.#handleCreateInvoiceAndNoteErrors(e)
        }
    }

    async sendInvoicesToErp(data: {
        invoiceIds: Guid[]
        headerText: string | null
        footerText: string | null
    }): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.sendInvoiceToErp,
                    rel: ApiLinkRel.new,
                },
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createFullCreditNote(link: IApiLink, data: ICreateFullCreditNoteDto): Promise<void> {
        try {
            await ApiService.followLink({ link, data })
        } catch (e) {
            await this.#handleCreateInvoiceAndNoteErrors(e)
        }
    }

    async createCollectiveCreditNote(link: IApiLink, data: ICreateCollectiveCreditNoteDto): Promise<void> {
        try {
            await ApiService.followLink({ link, data })
        } catch (e) {
            await this.#handleCreateInvoiceAndNoteErrors(e)
        }
    }

    async createPartialNote(link: IApiLink, data: ICreatePartialCreditNoteDto): Promise<void> {
        try {
            await ApiService.followLink({ link, data })
        } catch (e) {
            await this.#handleCreateInvoiceAndNoteErrors(e)
        }
    }

    async getOutgoingInvoices(params: IGetOutgoingInvoicesParams): Promise<GetOutgoingInvoices> {
        try {
            const data: IMultipleEntitiesResponse<IOutgoingInvoiceItemDto, { erpIncompleteInvoiceCount: number }> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.invoice,
                    rel: ApiLinkRel.all,
                },
                data: params,
            })

            const userIds = data.entities.map(dto => dto.createdBy)
            const userEmails = await UserService.getUserEmailsByIds(uniq(userIds))

            return {
                list: data.entities.map(dto => new OutgoingInvoiceItem(dto, {
                    createdByUserEmail: userEmails[dto.createdBy]
                })),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getOutgoingInvoices({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
                aggregates: {
                    linkBulkSyncErpInvoices: data.links?.find(link => link.entity === ApiLinkEntity.erpInvoicesSync && link.rel === ApiLinkRel.self),
                    erpIncompleteInvoiceCount: data.aggregates?.erpIncompleteInvoiceCount || 0,
                }
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async bulkSyncErpInvoices(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createAssetImageCollection({
        assetId,
        fileIds,
        tags,
    }: {
        assetId: Guid
        fileIds: Guid[]
        tags?: string[]
    }): Promise<Guid> {
        try {
            const resp: { id: Guid } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetImage,
                    rel: ApiLinkRel.new,
                    params: {
                        id: assetId
                    }
                },
                data: {
                    fileIds,
                    tags,
                }
            })
            return resp.id
        } catch (e) { }
        throw new CustomError({
            messageCode: "message.systemError"
        })
    }

    async deleteAssetImage(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async addDocuments(link: IApiLink, data: {
        documentCategoryId: Guid
        fileIds: Guid[]
    }): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async updateDocument(link: IApiLink, data: IUpdateDocumentDTO): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteDocument(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getCustomers(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
        erpOnly?: boolean
        currency?: Currency
    }): Promise<IMultipleEntities<CustomerItem>> {
        try {

            const {
                search,
                pageSize,
                pageNumber,
                erpOnly,
                currency,
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber,
                ErpOnly: erpOnly,
                currency,
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<ICustomerItemDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.customer,
                    rel: ApiLinkRel.all,
                    query,
                },
            })

            return {
                list: data.entities.map(dto => new CustomerItem(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getCustomers({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getReferenceNumbers(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
        type?: AssetReferenceNumberType
    }): Promise<IMultipleEntities<{ referenceNumber: string, assetsCount: number }>> {
        try {
            const {
                search,
                pageSize,
                pageNumber,
                type
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber,
                ReferenceNumberType: type
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<{ referenceNumber: string, assetsCount: number }> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.referenceNumber,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getReferenceNumbers({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getReferenceValues(params: {
        referenceNumber: string,
        pageSize?: number,
        pageNumber?: number
        type?: AssetReferenceNumberType
    }): Promise<GetReferenceValuesResponse> {
        try {
            const {
                referenceNumber,
                pageSize,
                pageNumber,
                type
            } = params

            const query: IApiFollowLink["query"] = {
                ReferenceNumber: referenceNumber,
                PageSize: pageSize,
                PageNumber: pageNumber,
                ReferenceNumberType: type
            }

            const data: IMultipleEntitiesResponse<
                {
                    assetId: Guid
                    articleId: Guid
                    lmid: Lmid
                    referenceValue?: IAmountWithCurrencyDto
                    isLabeled?: boolean
                },
                {
                    referenceValueSum: number
                }
            > = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.referenceValue,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            const assetIds = data.entities.map(asset => asset.assetId)
            const assetArticles = await ArticleService.getAssetArticles({
                assetIds,
                pageSize: assetIds.length,
            })

            return {
                list: data.entities.map(item => {
                    const assetArticle = assetArticles.list.find(aa => aa.assetId === item.assetId)
                    return {
                        ...item,
                        articleDescription: assetArticle?.articleDescription ?? "",
                        altArticleDescription: assetArticle?.altArticleDescription,
                        linkedToAssetCollection: assetArticle?.linkedToAssetCollection,
                        referenceValue: item.referenceValue ? new AmountWithCurrency(item.referenceValue) : undefined
                    }
                }),
                aggregates: {
                    referenceValueSum: data.aggregates?.referenceValueSum || 0
                },
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getReferenceValues({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getLabelGroup(id: string): Promise<LabelGroup> {
        try {
            const dto: IGetLabelGroupDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.labelGroup,
                    rel: ApiLinkRel.self,
                    params: { id }
                },
            })
            return new LabelGroup(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getLabelCategories(): Promise<LabelCategories> {
        try {
            const dto: IGetLabelCategoriesDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.labelCategory,
                    rel: ApiLinkRel.all,
                },
            })
            return new LabelCategories(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createLabelCategory(dto: ICreateLabelCategoryDto, link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: dto
            })
            return
        } catch (e) { }
        throw new CustomError({
            messageCode: "message.systemError"
        })
    }

    async getLabelsDashboard(): Promise<LabelsDashboard> {
        try {
            const dto: IGetLabelsDashboardDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.labelsDashboard,
                    rel: ApiLinkRel.all,
                },
            })
            return new LabelsDashboard(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getLabelsOverview(params: {
        articleNumbers?: string[]
        serialNumbers?: string[]
        serialNumbersMachine?: string[]
        inventoryNumbers?: string[]
        labels?: string[]
        manufacturerCodes?: string[]
        drawingNumbers?: string[]
        categoryIds?: (Guid | null)[]
        labelStatuses?: LabelStatus[]
        yearOfConstructions?: number[]
        pageNumber?: number
        pageSize?: number
    }): Promise<IMultipleEntities<LabelGroup>> {
        const {
            labelStatuses,
            yearOfConstructions,
            ...data
        } = params

        try {
            const resp: IMultipleEntitiesResponse<IGetLabelGroupDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.labelsOverview,
                    rel: ApiLinkRel.all,
                },
                data: {
                    ...data,
                    statuses: labelStatuses,
                    constructionYears: yearOfConstructions,
                }
            })

            return {
                list: resp.entities.map(dto => new LabelGroup(dto)),
                loadMore: hasMorePages(resp.paginationMetadata) ? (extendParams) => this.getLabelsOverview({ ...params, pageNumber: resp.paginationMetadata!.currentPage + 1, ...(extendParams || {}) }) : undefined
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateLabelGroup(link: IApiLink, data: IUpdateLabelGroupDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateLabelGroupLocation(link: IApiLink, data: IUpdateLabelGroupLocationDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async linkLabelGroupToAsset(link: IApiLink, assetId: Guid): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: { assetId }
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteLabelGroup(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getDocumentCategories(): Promise<DocumentCategories> {
        try {
            const dto: IGetDocumentCategoriesDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.documentCategory,
                    rel: ApiLinkRel.all,
                },
            })
            return new DocumentCategories(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async createDocumentCategory(dto: ICreateDocumentCategoryDto, link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: dto
            })
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response?.status === 409) {
                    throw new CustomError({
                        code: ErrorCode.conflict
                    })
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getAssetCategories(): Promise<AssetCategories> {
        try {
            const data: IGetAssetCategoriesDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetCategory,
                    rel: ApiLinkRel.all,
                },
            })
            return new AssetCategories(data)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getAssetLabelGroups(link: IApiLink): Promise<AssetLabelGroup[]> {
        try {
            const resp: IGetAssetLabelGroupDto[] = await ApiService.followLink({ link })
            return resp.map(dto => new AssetLabelGroup(dto))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async unlinkLabelGroupFromAsset(link: IApiLink, status?: LabelStatus): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    query: {
                        status
                    }
                }
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteLabelFromLabelGroup(link: IApiLink, label: string): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    query: {
                        label
                    }
                }
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetCategoriesImported(link: IApiLink): Promise<AssetCategoryImported[]> {
        try {
            const data: { entities?: IGetAssetCategoryImportedDto[] | null } = await ApiService.followLink({
                link,
            })
            return data.entities?.map(dto => new AssetCategoryImported(dto)) || []
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async createAssetCategory(params: ICreateAssetCategory): Promise<void> {
        try {
            await ApiService.followLink({
                link: params.createLink,
                data: params.dto
            })
        } catch (e) {
            if (!e.isAxiosError) {
                throw new CustomError({
                    code: ErrorCode.systemError
                })
            }

            const error = e as AxiosError<any>

            if (error.response?.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict
                })
            }

            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }
    async getCollectiveReferenceNumbers(): Promise<CollectiveReferenceNumbers> {
        try {
            const data: IGetCollectiveReferenceNumbersDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.collectiveReferenceNumber,
                    rel: ApiLinkRel.all,
                },
            })
            return new CollectiveReferenceNumbers(data)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createCollectiveReferenceNumber(link: IApiLink, data: ICollectiveReferenceNumberDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateCollectiveReferenceNumberRule(link: IApiLink, kind: CollectiveReferenceNumbersRule): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: {
                    kind
                },
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getAssetTypes(params?: {
        assetCategoryName?: string
        assetProductCode?: string
    }): Promise<AssetTypes> {
        try {
            const assetCategoryName = params?.assetCategoryName
            const assetProductCode = params?.assetProductCode
            const data: IGetAssetTypesDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetType,
                    rel: ApiLinkRel.all,
                    query: {
                        assetCategoryName,
                        assetProductCode,
                    },
                },
            })
            return new AssetTypes(data)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async createAssetType(params: ICreateAssetType): Promise<void> {
        try {
            await ApiService.followLink({
                link: params.createLink,
                data: params.dto
            })
        } catch (e) {
            if (!e.isAxiosError) {
                throw new CustomError({
                    code: ErrorCode.systemError
                })
            }

            const error = e as AxiosError<any>

            if (error.response?.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict
                })
            }

            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async updateAssetType(assetTypeId: Guid, link: IApiLink, patch: Operation[], eTag: string = ""): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    id: assetTypeId,
                },
                data: patch,
                headers: {
                    "If-Match": eTag
                }
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getNotifications(params: IGetNotificationsParams): Promise<IMultipleEntities<NotificationAny>> {
        try {
            const resp: IMultipleEntitiesResponse<IGetNotificationAnyDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.notification,
                    rel: ApiLinkRel.all,
                },
                data: params,
            })

            const userIds = uniq(resp.entities.filter(dto => !!dto.doneChangedBy).map(dto => dto.doneChangedBy)) as Guid[]
            const emails = await UserService.getUserEmailsByIds(uniq(userIds))

            return {
                list: resp.entities.map(dto => {
                    if (dto.doneChangedBy) {
                        dto.doneChangedByUserEmail = emails[dto.doneChangedBy]
                    }
                    switch (dto.notificationType) {
                        case NotificationType.assetsNotShipped:
                            return new NotificationAssetsNotShipped(dto as IGetNotificationAssetsNotShippedDto)
                        case NotificationType.assetOutOfOrder:
                            return new NotificationAssetOutOfOrder(dto as IGetNotificationAssetOutOfOrderDto)
                        case NotificationType.unplannedAssetReleased:
                            return new NotificationUnplannedAssetReleased(dto as IGetNotificationUnplannedAssetReleasedDto)
                        case NotificationType.unplannedAssetStored:
                            return new NotificationUnplannedAssetStored(dto as IGetNotificationUnplannedAssetStoredDto)
                        case NotificationType.assetDisposed:
                            return new NotificationAssetDisposed(dto as IGetNotificationAssetDisposedDto)
                        case NotificationType.storingCompleted:
                            return new NotificationStoringCompleted(dto as IGetNotificationStoringCompletedDto)
                        case NotificationType.storingIncomplete:
                            return new NotificationStoringIncomplete(dto as IGetNotificationStoringIncompleteDto)
                        case NotificationType.splitAssetFailed:
                            return new NotificationAssetSplittingFailed(dto as IGetNotificationAssetSplittingFailedDto)
                        case NotificationType.splitAssetSucceeded:
                            return new NotificationAssetSplittingSucceeded(dto as IGetNotificationAssetSplittingSucceededDto)
                        case NotificationType.assetReactivated:
                            return new NotificationAssetReactivated(dto as IGetNotificationAssetReactivatedDto)
                        case NotificationType.reactivateAssetFailed:
                            return new NotificationReactivateAssetFailed(dto as IGetNotificationReactivateAssetFailedDto)
                        case NotificationType.releaseCompleted:
                            return new NotificationReleaseCompleted(dto as IGetNotificationReleaseCompletedDto)
                        case NotificationType.manualReleaseCompleted:
                            return new NotificationManualReleaseCompleted(dto as IGetNotificationManualReleaseCompletedDto)
                        case NotificationType.manualStoreCompleted:
                            return new NotificationManualStoreCompleted(dto as IGetNotificationManualStoreCompletedDto)
                        case NotificationType.shippingOrderRejected:
                            return new NotificationShippingOrderRejected(dto as IGetNotificationShippingOrderRejectedDto)
                        case NotificationType.shippingOrderChanged:
                            return new NotificationShippingOrderChanged(dto)
                        case NotificationType.adHocReservationCreated:
                            return new NotificationAdHocReservationCreated(dto as IGetNotificationAdHocReservationCreatedDto)
                        case NotificationType.reservationCanceled:
                            return new NotificationReservationCanceled(dto as IGetNotificationReservationCanceledDto)
                        case NotificationType.collectiveReferenceNumberNotUpdated:
                            return new NotificationCollectiveReferenceNumberNotUpdated(dto as IGetNotificationCollectiveReferenceNumberNotUpdatedDto)
                        case NotificationType.collectiveReferenceNumberUpdated:
                            return new NotificationCollectiveReferenceNumberUpdated(dto as IGetNotificationCollectiveReferenceNumberUpdatedDto)
                        case NotificationType.referenceNumbersUpdated:
                            return new NotificationReferenceNumbersUpdated(dto as IGetNotificationReferenceNumbersUpdatedDto)
                        case NotificationType.assetInspected:
                            return new NotificationAssetInspectionPerformed(dto as IGetNotificationAssetInspectionPerformedDto)
                        case NotificationType.automaticInvoicingFailed:
                            return new NotificationAutomaticInvoicingFailed(dto as IGetNotificationAutomaticInvoicingFailedDto)
                        case NotificationType.assetMaintenanceTimeOverdueSoon:
                            return new NotificationAssetMaintenanceTimeOverdueSoon(dto as IGetNotificationAssetMaintenanceTimeOverdueSoonDto)
                        case NotificationType.assetMaintenanceOperationHoursOverdueSoon:
                            return new NotificationAssetMaintenanceOperationHoursOverdueSoon(dto as IGetNotificationAssetMaintenanceOperationHoursOverdueSoonDto)
                        default:
                            return null
                    }
                }).filter(n => !!n) as Array<NotificationAny>,
                pagination: resp.paginationMetadata,
                loadMore: hasMorePages(resp.paginationMetadata) ? () => this.getNotifications({ ...params, pageNumber: resp.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getTenantWarehouses(params?: IGetTenantWarehousesParams): Promise<TenantWarehouses> {
        try {
            const query = params?.articleId && params.start
                ? {
                    articleId: params.articleId,
                    start: toDateTimeWithoutTimezone(params.start),
                    end: params.end ? toDateTimeWithoutTimezone(params.end) : undefined,
                    skipReservationCartId: params.skipReservationCartId,
                    purpose: params.purpose,
                }
                : undefined
            const data: IGetTenantWarehousesDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.warehouses,
                    rel: ApiLinkRel.all,
                    query,
                },
            })
            return new TenantWarehouses(data)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createTenantWarehouse(link: IApiLink, data: ICreateTenantWarehouseDto): Promise<TenantWarehouse> {
        try {
            const dto: ITenantWarehouseDto = await ApiService.followLink({
                link,
                data,
            })
            return new TenantWarehouse(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateTenantWarehouse(link: IApiLink, data: ITenantWarehouseDto): Promise<TenantWarehouse> {
        try {
            const dto: ITenantWarehouseDto = await ApiService.followLink({
                link,
                data,
            })
            return new TenantWarehouse(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getStocktakingOrders(activeOnly?: boolean): Promise<StocktakingOrders> {
        try {
            const data: IMultipleEntitiesResponse<IGetStocktakingOrderDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.stocktakingOrder,
                    rel: ApiLinkRel.all,
                    query: {
                        ActiveOnly: activeOnly
                    },
                },

            })

            const userIds: Guid[] = []

            data.entities.forEach(soDto => {
                userIds.push(soDto.createdByUserId)
                if (soDto.closedByUserId) {
                    userIds.push(soDto.closedByUserId)
                }
                soDto.stocktakingScopes.forEach(ssDto => {
                    if (ssDto.scanningStartedByUserId) {
                        userIds.push(ssDto.scanningStartedByUserId)
                    }
                    if (ssDto.completedByUserId) {
                        userIds.push(ssDto.completedByUserId)
                    }
                    if (ssDto.closedByUserId) {
                        userIds.push(ssDto.closedByUserId)
                    }
                })
            })

            const tenantWarehouses = await this.getTenantWarehouses()
            const emails = await UserService.getUserEmailsByIds(uniq(userIds))

            return new StocktakingOrders(data, {
                tenantWarehouses: tenantWarehouses.entities,
                noWarehouseId: tenantWarehouses.noWarehouseId,
                emails
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createUpdateStocktakingOrder(data: ICreateUpdateStocktakingOrderDto, link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    // delete/close stocktaking order / reopen/close stocktaking scope
    async performStocktakingAction(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link,
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getStocktakingNotScannedAssets(params: {
        stocktakingId: Guid
        warehouseId: Guid
        pageSize?: number
        pageNumber?: number
        assetIds?: Guid[]
        excludeRegularScans: boolean
    }): Promise<IMultipleEntities<StocktakingNotScannedAsset, IStocktakingNotScannedAssetsAggregate | undefined>> {
        try {
            const {
                stocktakingId,
                warehouseId,
                pageSize,
                pageNumber,
                assetIds,
                excludeRegularScans,
            } = params

            const data: IGetStocktakingNotScannedAssetsDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.stocktakingNotScannedAssets,
                    rel: ApiLinkRel.all,
                    params: {
                        id: stocktakingId,
                        warehouseId,
                    },
                },
                data: {
                    pageSize,
                    pageNumber,
                    assetIds,
                    excludeRegularScans
                },
            })

            const isFirstPage = !pageNumber || pageNumber === 1
            let emails: { [userId: string]: string } | null = null
            let tenantWarehouses: TenantWarehouses | null = null

            if (isFirstPage) {
                const userIds: Guid[] = [
                    ...data.stocktakingScope.scanningStartedByUserId ? [data.stocktakingScope.scanningStartedByUserId] : [],
                    ...data.stocktakingScope.completedByUserId ? [data.stocktakingScope.completedByUserId] : [],
                    ...data.stocktakingScope.closedByUserId ? [data.stocktakingScope.closedByUserId] : [],
                ]
                emails = await UserService.getUserEmailsByIds(uniq(userIds))
                tenantWarehouses = await this.getTenantWarehouses()
            }

            let notScannedAssets: StocktakingNotScannedAsset[] = []
            const _assetIds = data.entities.map(a => a.assetId)

            if (_assetIds.length) {
                const assetArticles = await ArticleService.getAssetArticles({
                    assetIds: _assetIds,
                    pageSize: _assetIds.length,
                })
                notScannedAssets = data.entities.map(dto => {
                    const assetArticle = assetArticles.list.find(aa => aa.assetId === dto.assetId)
                    if (!assetArticle) return null
                    return new StocktakingNotScannedAsset(dto, {
                        lmid: assetArticle.lmid,
                        articleDescription: assetArticle.articleDescription || "",
                        articleId: assetArticle.articleId,
                        isLabeled: assetArticle.isLabeled,
                        isInCollection: assetArticle.linkedToAssetCollection,
                        linkUpdate: assetArticle.linkUpdate,
                    })
                }).filter(i => !!i) as StocktakingNotScannedAsset[]
            }

            return {
                aggregates: isFirstPage ? {
                    stocktakingDueDate: dayjs(data.stocktakingDueDate, "YYYY-MM-DD"),
                    stocktakingScope: new StocktakingScope(data.stocktakingScope, {
                        warehouseDescription: tenantWarehouses?.entities.find(tw => tw.id === data.stocktakingScope.warehouseId)?.description || "",
                        isNoWarehouse: tenantWarehouses?.noWarehouseId === data.stocktakingScope.warehouseId,
                        emails: emails || {},
                    }),
                } : undefined,
                list: notScannedAssets,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getStocktakingNotScannedAssets({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async recordStocktakingAsset(link: IApiLink, assetId: Guid): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: { assetId },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }

    }

    async createOrderAcknowledgement(link: IApiLink, isPreview: boolean = false): Promise<void> {
        try {
            const resp: { uri: string } = await ApiService.followLink({
                link,
                linkQuery: {
                    IsPreview: isPreview
                }
            })
            downloadUrlAsFile(resp.uri)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createRentalContract(link: IApiLink, isPreview: boolean = false): Promise<void> {
        try {
            const resp: { uri: string } = await ApiService.followLink({
                link,
                linkQuery: {
                    IsPreview: isPreview
                }
            })
            downloadUrlAsFile(resp.uri)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async uploadAssetImportFile(link: IApiLink, file: File): Promise<void> {
        try {
            const data = new FormData()
            data.append("file", file)
            await ApiService.followLink({ link, data })
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response?.status === 409) {
                    throw new CustomError({
                        code: ErrorCode.conflict,
                    })
                }
                if (error.response?.status === 422) {
                    throw new CustomError<AssetImportError[]>({
                        code: ErrorCode.unprocessableEntity,
                        payload: Object.keys(error.response.data.errors ?? []) as AssetImportError[],
                    })
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getAssetImportOperation(): Promise<AssetImportOperation> {
        try {
            const data: IGetAssetImportOperationDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetImport,
                    rel: ApiLinkRel.default,
                },
            })

            return new AssetImportOperation(data)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getAssetImportItem(id: Guid): Promise<AssetImportItem> {
        try {
            const data: IGetAssetImportItemDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetImportItem,
                    rel: ApiLinkRel.default,
                    params: { id },
                },
            })

            return new AssetImportItem(data)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async linkAssetImportItemToAsset(link: IApiLink, assetId: Guid) {
        try {
            await ApiService.followLink({
                link,
                data: { assetId },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteAssetImportOperation(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteAssetImportItems(link: IApiLink, assetImportItemIds: Guid[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: assetImportItemIds,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async importAssetImportItems(link: IApiLink, assetImportItemIds: Guid[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: assetImportItemIds,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetProducts({
        assetCategoryName,
        productType,
    }: {
        assetCategoryName: string
        productType: number
    }): Promise<AssetProduct[]> {
        try {
            const resp: { entities: IGetAssetProductDto[] } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetProduct,
                    rel: ApiLinkRel.all,
                    query: {
                        assetCategoryName,
                        productType,
                    }
                },

            })
            return resp.entities.map(dto => new AssetProduct(dto))
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getAssetDisplayStatuses(assetIds: Guid[]): Promise<Record<Guid, AssetDisplayStatus>> {
        if (!assetIds.length) return {}
        try {
            const resp: IGetAssetDisplayStatusDto[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetDisplayStatus,
                    rel: ApiLinkRel.all,
                },
                data: assetIds,
            })

            return resp.reduce((acc, dto) => {
                acc[dto.id] = new AssetDisplayStatus(dto)
                return acc
            }, {} as { [assetId: string]: AssetDisplayStatus })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    /**
     * Loads the distinct currencies of the tenant's ERP customers — the selectable request currencies.
     * The list is tenant-wide and stable within a session, so it is cached and concurrent calls share one request.
     */
    async getCustomerCurrencies({ ignoreCache }: { ignoreCache?: boolean } = {}): Promise<Currency[]> {
        if (!ignoreCache) {
            if (this.#customerCurrenciesCache) {
                return this.#customerCurrenciesCache
            }
            if (this.#customerCurrenciesRequest) {
                return this.#customerCurrenciesRequest
            }
        }

        const request = this.#requestCustomerCurrencies()
        this.#customerCurrenciesRequest = request
        request
            .catch(() => undefined)
            .finally(() => {
                if (this.#customerCurrenciesRequest === request) {
                    this.#customerCurrenciesRequest = null
                }
            })

        return request
    }

    async #requestCustomerCurrencies(): Promise<Currency[]> {
        try {
            const resp: Currency[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.customerCurrencies,
                    rel: ApiLinkRel.all,
                },
            })
            this.#customerCurrenciesCache = resp
            return resp
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getColors({ ignoreCache }: { ignoreCache?: boolean } = {}): Promise<Color[]> {
        if (!ignoreCache) {
            if (this.#colorsCache) {
                return this.#colorsCache
            }
            if (this.#colorsRequest) {
                return this.#colorsRequest
            }
        }

        const request = this.#requestColors()
        this.#colorsRequest = request
        request
            .catch(() => undefined)
            .finally(() => {
                if (this.#colorsRequest === request) {
                    this.#colorsRequest = null
                }
            })

        return request
    }

    async #requestColors(): Promise<Color[]> {
        try {
            const resp: IGetColorDto[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.color,
                    rel: ApiLinkRel.all,
                },
            })
            this.#colorsCache = resp.map(dto => new Color(dto))
            return this.#colorsCache
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createColor(dto: ICreateUpdateColorDto): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.color,
                    rel: ApiLinkRel.new,
                },
                data: dto,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateColor(id: Guid, dto: ICreateUpdateColorDto): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.color,
                    rel: ApiLinkRel.partialUpdate,
                    params: { id },
                },
                data: dto,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }


    async getOffers(params: IGetOffersParams): Promise<IMultipleEntities<OfferItem>> {
        try {
            const data: IMultipleEntitiesResponse<IGetOfferItemDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.offer,
                    rel: ApiLinkRel.all
                },
                data: params,
            })

            return {
                list: data.entities.map(dto => new OfferItem(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getOffers({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getOffer({
        offerId,
    }: {
        offerId: Guid
    }): Promise<Offer> {
        try {
            const dto: IGetOfferDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.offer,
                    rel: ApiLinkRel.self,
                    params: {
                        id: offerId
                    }
                },
                includeEtag: true,
            })

            return new Offer(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createOffer(data: ICreateOffer): Promise<{ id: Guid }> {
        try {
            const resp: { id: Guid } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.offer,
                    rel: ApiLinkRel.new,
                },
                data: Offer.toCreateDto(data),
            })
            return resp
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateOffer(link: IApiLink, eTag: string, data: IUpdateOffer): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: Offer.toUpdateDto(data),
                headers: {
                    "If-Match": eTag
                },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateOfferStatus(link: IApiLink, offerStatus: OfferStatus): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: { offerStatus },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteOffer(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async printOfferDocument(link: IApiLink): Promise<void> {
        try {
            const resp: { uri: string } = await ApiService.followLink({ link })
            downloadUrlAsFile(resp.uri)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getOfferNumbers({ search, includeRejected }: { search?: string, includeRejected?: boolean }): Promise<IMultipleEntities<string>> {
        return ApiService.getPagedData<string, string>({
            service: ApiServiceName.asset,
            entity: ApiLinkEntity.offerNumber,
            rel: ApiLinkRel.all,
            mapFunc: dto => dto,
            query: {
                Criteria: search,
                IncludeRejected: includeRejected,
            },
        })
    }

    async exportInventoryOverview(params: IExportInventoryOverviewParams): Promise<void> {
        try {
            const resp: { blob: Blob, fileName?: string } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetOverviewExport,
                    rel: ApiLinkRel.self,
                },
                data: params,
                headers: {
                    "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "blob",
                includeFilename: true,
            })

            downloadBlobAsFile(resp.blob, resp.fileName || "")
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetInspections({
        assetId,
        reservationId,
        shippingOrderId,
    }: IGetAssetInspectionsParams): Promise<AssetInspection[]> {
        try {
            const resp: IGetAssetInspectionDTO[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetInspection,
                    rel: ApiLinkRel.all,
                    params: {
                        query: {
                            assetId,
                            reservationId,
                            shippingOrderId,
                        },
                    },
                },
            })

            const userIds = resp.map(dto => dto.inspectedBy)
            const userEmails = await UserService.getUserEmailsByIds(uniq(userIds))

            return resp.map(dto => new AssetInspection(dto, { inspectedByUserEmail: userEmails[dto.inspectedBy] || "" }))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createAssetInspection(data: ICreateAssetInspectionDTO): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetInspection,
                    rel: ApiLinkRel.new,
                },
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateAssetInspection(link: IApiLink, data: IUpdateAssetInspectionDTO | IUpdateAssetInspectionCommentDTO): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async exportAssetInspection(query: { assetId: Guid, reservationId: Guid, deliveryInspectionId?: Guid, returnInspectionId?: Guid }): Promise<void> {
        try {
            // Build query parameters
            const params = new URLSearchParams({
                assetId: query.assetId,
                reservationId: query.reservationId,
            })

            if (query.deliveryInspectionId) {
                params.append('deliveryInspectionId', query.deliveryInspectionId)
            }

            if (query.returnInspectionId) {
                params.append('returnInspectionId', query.returnInspectionId)
            }

            // Use API service with blob response for PDF download
            const resp: { blob: Blob, fileName?: string } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetInspectionReport,
                    rel: ApiLinkRel.self,
                    params: {
                        query: {
                            assetId: query.assetId,
                            reservationId: query.reservationId,
                            deliveryInspectionId: query.deliveryInspectionId,
                            returnInspectionId: query.returnInspectionId,
                        }
                    }
                },
                headers: {
                    "Accept": "application/pdf",
                },
                responseType: "blob",
                includeFilename: true,
            })

            // Download the blob
            downloadBlobAsFile(resp.blob, resp.fileName || `inspection-report-${query.assetId}-${query.reservationId}.pdf`)

        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetInspectionPairs(assetId: Guid, reservationId?: Guid): Promise<AssetInspectionPair[]> {
        if (!reservationId) {
            return []
        }

        // Try the new InspectionPair API first since backend link is now available
        try {
            const data = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.inspectionPair,
                    rel: ApiLinkRel.all,
                    params: {
                        id: assetId,
                        query: {
                            reservationId: reservationId
                        }
                    }
                }
            })

            // Convert the response to AssetInspectionPair objects
            const pairs = data.map((dto: any) => {
                const pair = new AssetInspectionPair(dto)
                pair.reservationId = reservationId
                return pair
            })

            await this.populateInspectionPairDetails(pairs, assetId, reservationId)

            return pairs

        } catch (error) {
            // Fallback to the working method
            return this.getAssetInspectionPairsFallback(assetId, reservationId)
        }
    }

    // Helper method to populate inspection details in pairs
    private async populateInspectionPairDetails(pairs: AssetInspectionPair[], assetId: Guid, reservationId: Guid): Promise<void> {
        // Get all inspections for this asset and reservation
        const inspections = await this.getAssetInspections({ assetId, reservationId })

        // Map inspections to pairs by ID
        for (const pair of pairs) {
            const deliveryInspection = inspections.find(insp => insp.id === pair.deliveryInspectionId)
            const returnInspection = inspections.find(insp => insp.id === pair.returnInspectionId)

            if (deliveryInspection || returnInspection) {
                const pairInspections: AssetInspection[] = []
                if (deliveryInspection) pairInspections.push(deliveryInspection)
                if (returnInspection) pairInspections.push(returnInspection)
                pair.setInspections(pairInspections)
            }
        }
    }    // Fallback method using the old approach - creates pairs from individual inspections
    private async getAssetInspectionPairsFallback(assetId: Guid, reservationId?: Guid): Promise<AssetInspectionPair[]> {
        try {
            const inspections = await this.getAssetInspections({ assetId, reservationId })

            // Group inspections by shipping order ID to create proper pairs
            // Each shipping order should have a delivery and return inspection
            const inspectionGroups = new Map<string, { delivery?: AssetInspection, return?: AssetInspection }>()

            inspections.forEach(inspection => {
                if (!inspection.reservationId) return

                // Use shippingOrderId as the grouping key, or fallback to a unique key for each inspection
                const groupKey = inspection.shippingOrderId || `${inspection.reservationId}-${inspection.inspectionType}-${inspection.id}`

                if (!inspectionGroups.has(groupKey)) {
                    inspectionGroups.set(groupKey, {})
                }

                const group = inspectionGroups.get(groupKey)!
                if (inspection.inspectionType === 'delivery') {
                    group.delivery = inspection
                } else if (inspection.inspectionType === 'return') {
                    group.return = inspection
                }
            })

            // Convert groups to AssetInspectionPair objects
            const inspectionPairs: AssetInspectionPair[] = []
            inspectionGroups.forEach((group) => {
                // Get reservation ID from either delivery or return inspection
                const pairReservationId = group.delivery?.reservationId || group.return?.reservationId
                if (!pairReservationId) return

                const pair = new AssetInspectionPair({
                    reservationId: pairReservationId,
                    deliveryInspectionId: group.delivery?.id || null,
                    returnInspectionId: group.return?.id || null
                })
                pair.deliveryInspection = group.delivery || null
                pair.returnInspection = group.return || null
                inspectionPairs.push(pair)
            })

            return inspectionPairs
        } catch (e) {
            ErrorHandler.handleHttpError(e)
            return []
        }
    }

    async updateAssetAltArticleDescription(link: IApiLink, altArticleDescription: string): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: {
                    altArticleDescription,
                },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async exportConfiguration(params: IExportConfigurationParams): Promise<void> {
        try {
            const resp: { blob: Blob, fileName?: string } = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.configurationExport,
                    rel: ApiLinkRel.new,
                },
                data: {
                    ...params,
                    assets: params.assets.map(asset => ({
                        ...asset,
                        acquisitionDate: asset.acquisitionDate?.toISOString(),
                    })),
                },
                headers: {
                    "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "blob",
                includeFilename: true,
            })

            downloadBlobAsFile(resp.blob, resp.fileName || "")
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getRentToBuyChecklist({
        bookingPeriod,
        sortBy,
        sortDirection,
    }: {
        bookingPeriod: Dayjs
        sortBy?: RentToBuyChecklistSortBy
        sortDirection?: SortDirection
    }): Promise<RentToBuyChecklist> {
        try {
            const dto: IGetRentToBuyChecklistDTO = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.rentToBuyEvent,
                    rel: ApiLinkRel.all,
                    query: {
                        bookingPeriod: toDateWithoutTimezone(bookingPeriod),
                        sortBy,
                        sortDirection,
                    },
                },
            })

            return new RentToBuyChecklist(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async approveRentToBuyChecklistChanges(changeIds: Guid[]): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.rentToBuyEvent,
                    rel: ApiLinkRel.update,
                },
                data: changeIds,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getCommissions(): Promise<Commissions> {
        try {
            const dto: IGetCommissionsDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.commission,
                    rel: ApiLinkRel.self,
                },
            })
            return new Commissions(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateCommissions(linkUpdateCommissions: IApiLink, data: IUpdateCommissionsDto): Promise<void> {
        try {
            await ApiService.followLink({
                link: linkUpdateCommissions,
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAutocomplete({
        field,
        search,
    }: {
        field: AutocompleteField
        search?: string
    }): Promise<IMultipleEntities<string>> {
        return ApiService.getPagedData<string, string>({
            service: ApiServiceName.asset,
            entity: ApiLinkEntity.autocomplete,
            rel: ApiLinkRel.all,
            mapFunc: dto => dto,
            linkParams: { field },
            query: { Criteria: search },
        })
    }

    async getProfitCenterPermissions(): Promise<ProfitCenterPermissions> {
        try {
            const dto: IGetProfitCenterPermissionsItemDto[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.userProfitCenter,
                    rel: ApiLinkRel.all,
                },
            })
            return new ProfitCenterPermissions(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateProfitCenterPermissions(data: { userId: Guid, profitCenterIds: Guid[] }[]): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.userProfitCenter,
                    rel: ApiLinkRel.update,

                },
                data,
            })

        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    getGlobalAssetOverview(data: IGetGlobalAssetOverviewParams): {
        controller: AbortController,
        promise(): Promise<IGlobalAssetOverview>
    } {
        const controller = new AbortController()
        return {
            controller,
            promise: async () => {
                try {
                    const dto: IGetGlobalAssetOverviewDto = await ApiService.request({
                        serviceName: ApiServiceName.asset,
                        followLink: {
                            entity: ApiLinkEntity.globalAssetOverview,
                            rel: ApiLinkRel.all,
                        },
                        data,
                        signal: controller.signal
                    })

                    const articleDescriptions = new Map<Guid, IGlobalAssetOverviewInitParams>()

                    if (dto.entities.length > 0) {
                        const articleIds = uniq(dto.entities.map(e => e.articleId))
                        const articles = await ArticleService.getArticleAssets({
                            articleIds,
                            isOnlyForHolder: false,
                            pageSize: articleIds.length,
                        })

                        articles?.list.map(a => articleDescriptions.set(a.id, {
                            articleDescription: a.description,
                            articleNo: a.articleNo,
                        }))
                    }

                    return {
                        maxItemsExceeded: dto.maxItemsExceeded,
                        entities: dto.entities.map(e => new GlobalAssetOverviewItem(e, articleDescriptions.get(e.articleId) || {})),
                    }
                } catch (e) {
                    ErrorHandler.handleHttpError(e)
                }
            }
        }
    }

    async getGlobalAssetOverviewAsset(assetId: Guid): Promise<GlobalAssetOverviewAsset> {
        try {
            const dto: IGetGlobalAssetOverviewAssetDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.globalAssetOverview,
                    rel: ApiLinkRel.self,
                    params: { id: assetId },
                },
            })

            return new GlobalAssetOverviewAsset(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }


    async getGlobalAssetOverviewAutocompleteAutocomplete({
        field,
        search,
    }: {
        field: GlobalAssetOverviewAutocompleteField
        search?: string
    }): Promise<IMultipleEntities<string>> {
        return ApiService.getPagedData<string, string>({
            service: ApiServiceName.asset,
            entity: ApiLinkEntity.globalAssetOverviewAutocomplete,
            rel: ApiLinkRel.all,
            mapFunc: dto => dto,
            linkParams: { field },
            query: { Criteria: search },
        })
    }

    async getMaintenanceTypes(): Promise<MaintenanceType[]> {
        try {
            const data: IGetMaintenanceTypeDto[] = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.maintenanceType,
                    rel: ApiLinkRel.all,
                },
            })
            return data.map(dto => new MaintenanceType(dto))
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }


    async createMaintenanceType(data: ICreateMaintenanceTypeDto): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.maintenanceType,
                    rel: ApiLinkRel.new,
                },
                data,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetMaintenances(params: IGetAssetMaintenantcesParams): Promise<IMultipleEntities<AssetMaintenance>> {
        try {
            const data: IMultipleEntitiesResponse<IGetAssetMaintenanceDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.assetMaintenance,
                    rel: ApiLinkRel.all
                },
                data: params
            })
            return {
                list: data.entities.map(dto => new AssetMaintenance(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getAssetMaintenances({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createUpdateAssetMaintenance(link: IApiLink, data: ICreateUpdateAssetMaintenance): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: AssetMaintenance.getCreateUpdateDto(data),
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteAssetMaintenance(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async completeAssetMaintenance(link: IApiLink, data: ICompleteAssetMaintenance): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: AssetMaintenance.getCompleteDto(data),
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getWorkingDaysCalendars(): Promise<WorkingDaysCalendars> {
        try {
            const dto: IGetWorkingDaysCalendarsDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.workingDaysCalendar,
                    rel: ApiLinkRel.all
                }
            })
            return new WorkingDaysCalendars(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createUpdateWorkingDaysCalendar(link: IApiLink, data: ICreateUpdateWorkingDaysCalendarDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data
            })
        }
        catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getHolidays(params: { year?: number } = {}): Promise<Holidays> {
        try {
            const dto: IGetHolidaysDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.holiday,
                    rel: ApiLinkRel.all,
                    query: params,
                },
            })
            return new Holidays(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createUpdateHoliday(link: IApiLink, data: ICreateUpdateHoliday): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: Holiday.getCreateUpdateDto(data),
            })
        }
        catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getCustomersDashboard(params: IGetCustomersDashboardParams): Promise<IMultipleEntities<CustomersDashboardItem>> {
        try {
            const data: IMultipleEntitiesResponse<IGetCustomersDashboardItemDto> = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.customersDashboard,
                    rel: ApiLinkRel.all
                },
                data: params
            })
            return {
                list: data.entities.map(dto => new CustomersDashboardItem(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getCustomersDashboard({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getErpCustomer(customerNumber: string): Promise<ErpCustomer> {
        try {
            const dto: IGetErpCustomerDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.customerDetails,
                    rel: ApiLinkRel.self,
                    params: { customerNumber },
                }
            })
            return new ErpCustomer(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    /**
     * Looks up a customer by number without raising a system error toast on absence.
     * Returns the customer if it exists in MP, or `null` if it is not found (404).
     * Used to distinguish the "customer number not found in ERP" case after an
     * on-demand refresh completes.
     */
    async findErpCustomer(customerNumber: string): Promise<ErpCustomer | null> {
        try {
            const dto: IGetErpCustomerDto = await ApiService.request({
                serviceName: ApiServiceName.asset,
                followLink: {
                    entity: ApiLinkEntity.customerDetails,
                    rel: ApiLinkRel.self,
                    params: { customerNumber },
                }
            })
            return new ErpCustomer(dto)
        } catch (e) {
            if (e?.isAxiosError && (e as AxiosError).response?.status === 404) {
                return null
            }
            ErrorHandler.handleHttpError(e)
            return null
        }
    }

    async getErpCustomerDocuments(link: IApiLink): Promise<Document[]> {
        try {
            const resp: IGetDocumentDto[] = await ApiService.followLink({ link })

            const userIds = uniq(resp.map(d => d.uploadedBy))
            const userEmails = userIds.length
                ? await UserService.getUserEmailsByIds(userIds)
                : {}

            return resp.map(dto => new Document(dto, {
                entity: ApiLinkEntity.customerDocuments,
                uploadedByEmail: userEmails[dto.uploadedBy]
            }))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async addErpCustomerDocuments(link: IApiLink, fileIds: Guid[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: { fileIds },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteErpCustomerDocument(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async resubmitInvoice(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async patchOutgoingInvoiceOrderNumber(link: IApiLink, erpOrderNumber: string): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                headers: { "Content-Type": "application/merge-patch+json" },
                data: { erpOrderNumber },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteOutgoingInvoice(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }
}

const AssetServiceExtent = ExtendAsSelfPopulatable(AssetService, ApiServiceName.asset)

export default new AssetServiceExtent()
